import type { DomainPriority, FitnessDomain } from '~/models/fitnessDomain'
import { type Confidence, isOpinion, type RulesOutcome } from '~/models/rulesOpinion'

/**
 * One combined answer from several domains (D-090, D-098).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE ACTION, NOT ONE PANEL PER DOMAIN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A user with three active domains must not be shown three equal
 *  recommendations. That is not a display preference: the product's whole claim
 *  is that Project Sola decides what matters this week, and a screen offering
 *  three things of equal weight has handed the prioritising back to the user —
 *  the one job it exists to do.
 *
 *  So: **one overall status, one primary action, at most two quieter supporting
 *  actions.** Per-domain detail is available, on a different screen, behind a
 *  deliberate tap.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NOTHING HERE INVENTS AN OPINION
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Every action returned is one a ruleset produced. This selects and orders; it
 *  never composes advice, never merges two actions into a third, and never
 *  promotes a domain the engine had nothing to say about. A domain whose rules
 *  are unimplemented is carried through as `quiet` with its own reason, so the
 *  user can see that Project Sola is not judging it rather than being left to
 *  infer silence means approval.
 *
 *  Pure, and in `rules/` deliberately: no clock, no storage, no formatting. The
 *  `evaluatedAt` on each outcome is already stamped by the engine that produced
 *  it.
 */

export interface DomainOutcome {
  readonly domain: FitnessDomain
  readonly priority: DomainPriority
  readonly outcome: RulesOutcome
}

/** A domain Project Sola is not speaking about, and why. */
export interface QuietDomain {
  readonly domain: FitnessDomain
  readonly reason: string
}

export interface CombinedOpinion {
  /** `nothingToSay` when no domain produced an opinion at all. */
  readonly status: 'opinion' | 'nothingToSay'
  /** The one action. Absent only when `status` is `nothingToSay`. */
  readonly primaryAction?: string
  /** Never more than two. */
  readonly supportingActions: readonly string[]
  /** The confidence of the opinion the primary action came from, unaltered. */
  readonly confidence?: Confidence
  /** Which domain the primary action came from, so the screen can say so. */
  readonly primaryDomain?: FitnessDomain
  /** Everything, in the order it was considered. */
  readonly considered: readonly DomainOutcome[]
  readonly quiet: readonly QuietDomain[]
}

/**
 * Ordering, not weights.
 *
 * The same shape as `RULE_FAMILY_PRECEDENCE`: a list, so that "primary beats
 * supporting" is a fact about position rather than a number someone can tune.
 * Within one priority the domain's own declaration order breaks the tie, which is
 * arbitrary but stable — and stability is what matters, because a screen whose
 * lead action changed between two equally-ranked domains on every render would be
 * unusable.
 */
const PRIORITY_ORDER: readonly DomainPriority[] = ['primary', 'supporting', 'protect', 'none']

const MAX_SUPPORTING = 2

/**
 * Combine per-domain outcomes into the single answer a screen shows.
 *
 * The input is expected to be one entry per **participating** domain. Filtering
 * paused, ended and track-only goals happens in `services/`, where the records
 * are — this function does not read a goal and cannot decide participation.
 */
export function combineDomainOpinions(outcomes: readonly DomainOutcome[]): CombinedOpinion {
  const considered = [...outcomes].sort(
    (left, right) => PRIORITY_ORDER.indexOf(left.priority) - PRIORITY_ORDER.indexOf(right.priority),
  )

  const quiet = considered.filter((entry) => !isOpinion(entry.outcome)).map(quietReasonFor)
  const speaking = considered.filter(
    (entry): entry is DomainOutcome & { outcome: Extract<RulesOutcome, { outcome: 'opinion' }> } =>
      isOpinion(entry.outcome),
  )

  const lead = speaking[0]
  if (lead === undefined) {
    /* Every active domain is unjudgeable, or there are none. Not an error and not
       an empty screen: the caller shows the quiet reasons, which is the honest
       thing a mobility-only user should see. */
    return { status: 'nothingToSay', supportingActions: [], considered, quiet }
  }

  /**
   * The supporting slots, filled in one deliberate order.
   *
   * The lead domain's own supporting actions come first, because they were
   * produced by a ruleset that had already seen the whole week and decided they
   * belonged beside its primary action. Other domains' primary actions come after
   * — they are more important *to their domain*, but this list is about the one
   * week the user is looking at, and the lead domain is the one they said matters
   * most.
   */
  const supportingActions = [
    ...lead.outcome.supportingActions,
    ...speaking.slice(1).map((entry) => entry.outcome.primaryAction),
  ]
    .filter((action) => action !== lead.outcome.primaryAction)
    .filter(unique)
    .slice(0, MAX_SUPPORTING)

  return {
    status: 'opinion',
    primaryAction: lead.outcome.primaryAction,
    supportingActions,
    confidence: lead.outcome.confidence,
    primaryDomain: lead.domain,
    considered,
    quiet,
  }
}

/**
 * Why a domain said nothing, in the words the outcome itself carries.
 *
 * Never composed here. Each terminal outcome already knows its own reason —
 * `domainRulesUnavailable` from the registry, the phase ones from the engine —
 * and restating them at the call site is how two screens end up explaining the
 * same absence differently.
 */
function quietReasonFor(entry: DomainOutcome): QuietDomain {
  const { domain, outcome } = entry

  if (outcome.outcome === 'domainRulesUnavailable') {
    return { domain, reason: outcome.reason }
  }
  if (outcome.outcome === 'phaseRulesUnavailable') {
    return {
      domain,
      reason: 'This build has no rules for the phase on record, so it is not judging this week.',
    }
  }
  return {
    domain,
    reason: 'There is no active phase, so there is nothing to read this week against.',
  }
}

function unique<T>(value: T, index: number, all: readonly T[]): boolean {
  return all.indexOf(value) === index
}
